import * as i0 from '@angular/core';
import { ErrorHandler, APP_INITIALIZER, Injectable, InjectionToken, NgModule, Optional, SkipSelf } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule } from '@angular/common/http';
import * as Sentry from '@sentry/angular-ivy';
import { Router } from '@angular/router';
import { CaptureConsole } from '@sentry/integrations';

class LogLevelInfo {
    static { this.TRACE = new LogLevelInfo('trace', 0); }
    static { this.DEBUG = new LogLevelInfo('debug', 1); }
    static { this.INFO = new LogLevelInfo('info', 2); }
    static { this.WARN = new LogLevelInfo('warn', 3); }
    static { this.ERROR = new LogLevelInfo('error', 4); }
    static { this.levels = [LogLevelInfo.TRACE, LogLevelInfo.DEBUG, LogLevelInfo.INFO, LogLevelInfo.WARN, LogLevelInfo.ERROR]; }
    constructor(id, value) {
        this.id = id;
        this.value = value;
    }
    static { this.of = (logLevelId) => {
        const level = LogLevelInfo.levels
            .find(l => l.id === logLevelId);
        if (!level) {
            throw new Error(`unknown loglevel passed: ${logLevelId}`);
        }
        return level;
    }; }
    static { this.getLevels = (logLevelId) => {
        const level = LogLevelInfo.of(logLevelId);
        return LogLevelInfo.levels.slice(level.value, LogLevelInfo.levels.length);
    }; }
}

/**
 * Domain Helpers
 * */
const createRelease = (options) => `${options.appName}@${options.version}`;
const isAdvancedMonitoringEnabled = (monitoringType) => monitoringType === "ADVANCED" /* LogMonitoringType.ADVANCED */;
const getAppLogLevel = (logLevel) => logLevel || LogLevelInfo.WARN.id;
/**
 * Sentry Helpers
 * */
const mapLogLevelToSentrySeverity = (logLevel) => {
    logLevel = getAppLogLevel(logLevel);
    switch (logLevel) {
        case 'trace':
        case 'debug':
            return 'debug';
        case 'error':
            return 'error';
        case 'info':
            return 'info';
        case 'warn':
            return 'warning';
    }
};
const getCaptureLogLevels = (logLevel) => {
    logLevel = getAppLogLevel(logLevel);
    return LogLevelInfo.getLevels(logLevel).map(l => mapLogLevelToSentrySeverity(l.id));
};

class SentryErrorHandlerService {
    constructor() {
    }
    static { this.initSentry = (sentryConfig) => {
        if (!sentryConfig || !sentryConfig.dsn || sentryConfig.monitoringType === "OFF" /* LogMonitoringType.OFF */) {
            return;
        }
        const commonOptions = this.getCommonOptions(sentryConfig);
        const defaultOptions = this.getDefaultOptions(sentryConfig);
        SentryErrorHandlerService.logLevel = defaultOptions.logLevel;
        const simpleMonitoringOptions = SentryErrorHandlerService.getSimpleMonitoringOptions(defaultOptions);
        switch (defaultOptions.monitoringType.toString()) {
            case "SIMPLE" /* LogMonitoringType.SIMPLE */:
                Sentry.init({
                    ...commonOptions,
                    ...simpleMonitoringOptions,
                    defaultIntegrations: false,
                });
                break;
            case "ADVANCED" /* LogMonitoringType.ADVANCED */:
                const advancedMonitoringOptions = SentryErrorHandlerService.getAdvancedMonitoringOptions(defaultOptions);
                Sentry.init({
                    ...commonOptions,
                    ...simpleMonitoringOptions,
                    ...advancedMonitoringOptions,
                });
                break;
            case "OFF" /* LogMonitoringType.OFF */:
                return;
        }
    }; }
    static getAppLogLevel() {
        return LogLevelInfo.of(SentryErrorHandlerService.logLevel || 'warn');
    }
    static { this.getCommonOptions = (options) => ({
        dsn: options.dsn,
        release: createRelease(options)
    }); }
    static { this.getDefaultOptions = (options) => ({
        monitoringType: options.monitoringType || "SIMPLE" /* LogMonitoringType.SIMPLE */,
        logLevel: getAppLogLevel(options.logLevel),
        dsn: options.dsn,
        appName: options.appName,
        version: options.version
    }); }
    static { this.getSimpleMonitoringOptions = (options) => ({
        'initialScope': (scope) => {
            scope.setLevel(mapLogLevelToSentrySeverity(options.logLevel));
            return scope;
        },
        tracesSampleRate: 1.0
    }); }
    static { this.getAdvancedMonitoringOptions = (options) => ({
        integrations: () => {
            return [
                new Sentry.BrowserTracing({
                    routingInstrumentation: Sentry.routingInstrumentation,
                }),
                new CaptureConsole({
                    levels: getCaptureLogLevels(options.logLevel)
                }),
                new Sentry.Replay(),
            ];
        },
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1.0,
    }); }
    static getSentryProviders(monitoringType) {
        if (!isAdvancedMonitoringEnabled(monitoringType)) {
            return [];
        }
        return [
            {
                provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false })
            },
            { provide: Sentry.TraceService, deps: [Router] },
            {
                provide: APP_INITIALIZER, useFactory: () => () => {
                }, deps: [Sentry.TraceService], multi: true
            }
        ];
    }
}

class LoggerService {
    constructor() {
        this.appLogLevel = SentryErrorHandlerService.getAppLogLevel();
    }
    logDebug(...message) {
        this.writeToLog(LogLevelInfo.DEBUG.value, ...message);
    }
    log(...message) {
        this.writeToLog(LogLevelInfo.INFO.value, ...message);
    }
    logWarn(...message) {
        this.writeToLog(LogLevelInfo.WARN.value, ...message);
    }
    logError(...message) {
        this.writeToLog(LogLevelInfo.ERROR.value, ...message);
    }
    logTrace(...message) {
        this.writeToLog(LogLevelInfo.TRACE.value, ...message);
    }
    /**
     * Write logs.
     */
    writeToLog(level, ...message) {
        if (level >= this.appLogLevel.value) {
            if (level === LogLevelInfo.INFO.value) {
                console.info(...message);
            }
            else if (level === LogLevelInfo.ERROR.value) {
                console.error(...message);
            }
            else if (level === LogLevelInfo.WARN.value) {
                console.warn(...message);
            }
            else if (level === LogLevelInfo.TRACE.value) {
                console.trace(...message);
            }
            else if (level === LogLevelInfo.DEBUG.value) {
                console.debug(...message);
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: LoggerService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: LoggerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: LoggerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const LOGGER_CONFIG = new InjectionToken('LOGGER_CONFIG_PARAMS');
class CidaasLoggerUiSdkModule {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('CidaasLoggerUiSdkModule is already loaded. Please add it in AppModule only.');
        }
    }
    static forRoot(conf) {
        return {
            ngModule: CidaasLoggerUiSdkModule,
            providers: [
                { provide: LOGGER_CONFIG, useValue: conf },
                ...SentryErrorHandlerService.getSentryProviders(conf?.monitoringType)
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: CidaasLoggerUiSdkModule, deps: [{ token: CidaasLoggerUiSdkModule, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.8", ngImport: i0, type: CidaasLoggerUiSdkModule, imports: [BrowserModule,
            HttpClientModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: CidaasLoggerUiSdkModule, imports: [BrowserModule,
            HttpClientModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.8", ngImport: i0, type: CidaasLoggerUiSdkModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        BrowserModule,
                        HttpClientModule
                    ]
                }]
        }], ctorParameters: function () { return [{ type: CidaasLoggerUiSdkModule, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; } });

/*
 * Public API Surface of cidaas-logger-ui-sdk
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CidaasLoggerUiSdkModule, LOGGER_CONFIG, LogLevelInfo, LoggerService, SentryErrorHandlerService };
